import { useMutation } from "@apollo/client/react"
import { CircularProgress, Alert, Button } from "@mui/material"
import { X, Check } from "lucide-mui"
import React, { useState, useMemo, useCallback, useEffect } from "react"

import { SETTINGS_SECTION_STYLES } from "@/components/settings/_styles"
import {
  UpdateVenueTagsDocument,
  type UpdateVenueTagsMutation,
  type UpdateVenueTagsMutationVariables,
} from "@/graphql/graphql"
import i18n from "@/i18n"

type VenueTag = {
  category: string
  value: string
  label: string
  position: number
  custom: boolean
}

type TagOption = {
  value: string
  label: string
  subcategory?: string | null
}

type CategoryDefinition = {
  key: string
  selectionType: string
  maxSelections?: number | null
  allowCustom: boolean
  label: string
  descriptionText?: string | null
  options: TagOption[]
}

type VenueInformationProps = {
  venueId: string
  tags: VenueTag[]
  definitions: CategoryDefinition[]
  onRefetch: () => Promise<unknown> | void
  onStateChange?: (state: CategoryState) => void
}

export type CategoryState = Record<string, string[]>

const INITIAL_OPTIONS_LIMIT = 6

export const buildStateFromTags = (tags: VenueTag[]): CategoryState => {
  const state: CategoryState = {}
  if (!tags) return state
  for (const tag of tags) {
    if (!state[tag.category]) state[tag.category] = []
    state[tag.category].push(tag.value)
  }
  return state
}

const STYLES = {
  card: "border border-solid border-[#e6e6e6] rounded-[10px] p-5",
  titleSemibold: "text-sm font-semibold text-black",
  titleMedium: "text-sm font-medium text-black",
  description: "text-[10px] text-[#616161] mt-1",
  pillContainer: "flex flex-wrap gap-2 mt-3",
  pillBase:
    "px-3 py-1.5 rounded-[5px] text-xs font-normal transition-all cursor-pointer border border-solid outline-none appearance-none",
  pillSelected: "bg-[#f5f7f9] text-[#6b7280] border-[#b0b4bd]",
  pillUnselected: "bg-white text-[#6b7280] border-[#f5f7f9] hover:border-gray-300",
  pillRemovable: "bg-[#f5f7f9] text-[#78716c] border-[#f5f7f9] flex items-center gap-2.5",
  pillMultiSelected: "bg-[#f5f7f9] text-[#6b7280] border-[#f5f7f9] flex items-center gap-2.5",
  pillVenueTypeSelected: "bg-[#f5f7f9] text-[#78716c] border-[#f5f7f9] flex items-center gap-2.5",
  removeButton: "border-none bg-transparent p-0 cursor-pointer hover:text-gray-700 transition-colors",
  addButton:
    "px-3 py-1.5 rounded-[5px] text-xs font-normal bg-white text-[#6b7280] border border-solid border-[#f5f7f9] hover:border-gray-300 cursor-pointer outline-none",
  addInputContainer: "flex items-center gap-1 px-3 py-1 rounded-[5px] border border-solid border-blue-500 bg-white",
  addInput: "outline-none text-xs min-w-[80px] bg-transparent border-none",
  showMore:
    "text-xs font-medium text-[#6b7686] mt-3 cursor-pointer hover:text-gray-600 border-none bg-transparent p-0 outline-none",
  subcategoryLabel: "text-xs font-normal text-black uppercase",
  gridPillBase:
    "px-2 py-1 rounded-[5px] text-xs font-normal transition-all cursor-pointer border border-solid outline-none appearance-none bg-[#f5f7f9] text-[#6b7280]",
  gridPillSelected: "border-[#6b7280]",
  gridPillUnselected: "border-transparent",
} as const

const SingleSelectPills = ({
  options,
  selected,
  onChange,
}: {
  options: TagOption[]
  selected: string[]
  onChange: (values: string[]) => void
}) => (
  <div className={STYLES.pillContainer}>
    {options.map((opt) => {
      const isSelected = selected.includes(opt.value)
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(isSelected ? [] : [opt.value])}
          className={`${STYLES.pillBase} ${isSelected ? STYLES.pillSelected : STYLES.pillUnselected}`}
        >
          {opt.label}
        </button>
      )
    })}
  </div>
)

const SingleCustomInput = ({ selected, onChange }: { selected: string[]; onChange: (values: string[]) => void }) => {
  const value = selected[0] || ""

  return (
    <div className="mt-3">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          const nextValue = e.target.value
          onChange(nextValue.trim() ? [nextValue] : [])
        }}
        placeholder={i18n.t("app.settings.venues.venue_information.add_placeholder")}
        className="w-full rounded-[5px] border border-solid border-[#e6e6e6] px-3 py-2 text-sm text-[#201f1f] outline-none focus:border-blue-500"
      />
    </div>
  )
}

const MultiSelectPills = ({
  options,
  selected,
  onChange,
  allowCustom,
  maxSelections,
}: {
  options: TagOption[]
  selected: string[]
  onChange: (values: string[]) => void
  allowCustom: boolean
  maxSelections?: number | null
}) => {
  const [isAdding, setIsAdding] = useState(false)
  const [newValue, setNewValue] = useState("")
  const [expanded, setExpanded] = useState(false)

  const knownValues = options.map((o) => o.value)
  const customValues = selected.filter((v) => !knownValues.includes(v))

  const visibleOptions = expanded ? options : options.slice(0, INITIAL_OPTIONS_LIMIT)
  const hasMore = options.length > INITIAL_OPTIONS_LIMIT

  const atMax = maxSelections != null && selected.length >= maxSelections

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else if (!atMax) {
      onChange([...selected, value])
    }
  }

  const handleRemoveCustom = (value: string) => {
    onChange(selected.filter((v) => v !== value))
  }

  const handleAddCustom = () => {
    const trimmed = newValue.trim()
    const hasDuplicate = selected.some((value) => value.toLowerCase() === trimmed.toLowerCase())

    if (trimmed && !hasDuplicate && !atMax) {
      onChange([...selected, trimmed])
    }
    setNewValue("")
    setIsAdding(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleAddCustom()
    } else if (e.key === "Escape") {
      setNewValue("")
      setIsAdding(false)
    }
  }

  return (
    <>
      <div className={STYLES.pillContainer}>
        {visibleOptions.map((opt) => {
          const isSelected = selected.includes(opt.value)
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleToggle(opt.value)}
              disabled={!isSelected && atMax}
              className={`${STYLES.pillBase} ${isSelected ? STYLES.pillMultiSelected : STYLES.pillUnselected} ${!isSelected && atMax ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {opt.label}
              {isSelected ? <X className="w-2 h-2" /> : null}
            </button>
          )
        })}

        {customValues.map((value) => (
          <span key={value} className={`${STYLES.pillBase} ${STYLES.pillRemovable}`}>
            {value}
            <button type="button" onClick={() => handleRemoveCustom(value)} className={STYLES.removeButton}>
              <X className="w-2 h-2" />
            </button>
          </span>
        ))}

        {allowCustom ? (
          isAdding ? (
            <div className={STYLES.addInputContainer}>
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                  if (!newValue.trim()) setIsAdding(false)
                }}
                placeholder={i18n.t("app.settings.venues.venue_information.add_placeholder")}
                className={STYLES.addInput}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- autofocus is intentional for this input
                autoFocus
              />
              <button
                type="button"
                onClick={handleAddCustom}
                className="border-none bg-transparent p-0 cursor-pointer text-green-600"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setIsAdding(true)} className={STYLES.addButton}>
              + {i18n.t("app.settings.venues.venue_information.add")}
            </button>
          )
        ) : null}
      </div>

      {hasMore ? (
        <button type="button" onClick={() => setExpanded(!expanded)} className={STYLES.showMore}>
          {expanded
            ? `- ${i18n.t("app.settings.venues.venue_information.show_less")}`
            : `+ ${i18n.t("app.settings.venues.venue_information.show_more")}`}
        </button>
      ) : null}
    </>
  )
}

const VenueTypeSelector = ({
  options,
  selected,
  onChange,
  maxSelections,
}: {
  options: TagOption[]
  selected: string[]
  onChange: (values: string[]) => void
  maxSelections?: number | null
}) => {
  const [expanded, setExpanded] = useState(false)
  const atMax = maxSelections != null && selected.length >= maxSelections

  const subcategories = useMemo(() => {
    const groups: Record<string, TagOption[]> = {}
    for (const opt of options) {
      const sub = opt.subcategory || "other"
      if (!groups[sub]) groups[sub] = []
      groups[sub].push(opt)
    }
    return groups
  }, [options])

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else if (!atMax) {
      onChange([...selected, value])
    }
  }

  const selectedPills = options.filter((opt) => selected.includes(opt.value))

  return (
    <>
      <div className={STYLES.pillContainer}>
        {selectedPills.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleToggle(opt.value)}
            className={`${STYLES.pillBase} ${STYLES.pillVenueTypeSelected}`}
          >
            {opt.label}
            <X className="w-2 h-2" />
          </button>
        ))}
        <button type="button" onClick={() => setExpanded(!expanded)} className={STYLES.addButton}>
          + {i18n.t("app.settings.venues.venue_information.add")}
        </button>
      </div>

      <button type="button" onClick={() => setExpanded(!expanded)} className={STYLES.showMore}>
        {expanded
          ? `- ${i18n.t("app.settings.venues.venue_information.show_less_options")}`
          : `+ ${i18n.t("app.settings.venues.venue_information.show_more_options")}`}
      </button>

      {expanded ? (
        <>
          <hr className="border-t border-solid border-[#e6e6e6] mt-3" />
          <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-6">
            {Object.entries(subcategories).map(([sub, opts]) => (
              <div key={sub}>
                <div className={STYLES.subcategoryLabel}>
                  {i18n.t(`app.venue_tags.venue_type.subcategories.${sub}`, { defaultValue: sub.replace(/_/g, " ") })}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {opts.map((opt) => {
                    const isSelected = selected.includes(opt.value)
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleToggle(opt.value)}
                        disabled={!isSelected && atMax}
                        className={`${STYLES.gridPillBase} ${isSelected ? STYLES.gridPillSelected : STYLES.gridPillUnselected} ${!isSelected && atMax ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </>
  )
}

const CategoryCard = ({
  definition,
  selected,
  onChange,
}: {
  definition: CategoryDefinition
  selected: string[]
  onChange: (values: string[]) => void
}) => {
  const hasSubcategories = definition.options.some((o) => o.subcategory)
  const isSingleSelect = definition.selectionType === "single"
  const isSingleCustom = isSingleSelect && definition.allowCustom && definition.options.length === 0
  const titleStyle = isSingleSelect ? STYLES.titleMedium : STYLES.titleSemibold

  return (
    <div className={STYLES.card}>
      <div className={titleStyle}>{definition.label}</div>
      {definition.descriptionText ? <div className={STYLES.description}>{definition.descriptionText}</div> : null}

      {hasSubcategories ? (
        <VenueTypeSelector
          options={definition.options}
          selected={selected}
          onChange={onChange}
          maxSelections={definition.maxSelections}
        />
      ) : isSingleCustom ? (
        <SingleCustomInput selected={selected} onChange={onChange} />
      ) : isSingleSelect ? (
        <SingleSelectPills options={definition.options} selected={selected} onChange={onChange} />
      ) : (
        <MultiSelectPills
          options={definition.options}
          selected={selected}
          onChange={onChange}
          allowCustom={definition.allowCustom}
          maxSelections={definition.maxSelections}
        />
      )}
    </div>
  )
}

const VenueInformation: React.FC<VenueInformationProps> = ({
  venueId,
  tags,
  definitions,
  onRefetch,
  onStateChange,
}) => {
  const [updateTags, { loading: saving }] = useMutation<UpdateVenueTagsMutation, UpdateVenueTagsMutationVariables>(
    UpdateVenueTagsDocument,
  )

  const [localState, setLocalState] = useState<CategoryState>(() => buildStateFromTags(tags))
  const [dirty, setDirty] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const serverState = useMemo(() => buildStateFromTags(tags), [tags])

  useEffect(() => {
    if (!dirty) setLocalState(serverState)
  }, [serverState, dirty])

  useEffect(() => {
    onStateChange?.(localState)
  }, [localState, onStateChange])

  const handleCategoryChange = useCallback(
    (categoryKey: string) => (values: string[]) => {
      setLocalState((prev) => ({ ...prev, [categoryKey]: values }))
      setDirty(true)
      setSuccessMessage(null)
    },
    [],
  )

  const handleSave = async () => {
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const result = await updateTags({
        variables: { venueId, tags: localState },
      })

      if (result.data?.updateVenueTags?.success) {
        await onRefetch()
        setDirty(false)
        setSuccessMessage(i18n.t("app.settings.venues.venue_information.saved"))
      } else {
        const errors = result.data?.updateVenueTags?.errors
        setErrorMessage(errors?.[0]?.message || i18n.t("app.settings.venues.venue_information.error"))
      }
    } catch {
      setErrorMessage(i18n.t("app.settings.venues.venue_information.error"))
    }
  }

  const handleReset = () => {
    setLocalState(serverState)
    setDirty(false)
    setSuccessMessage(null)
    setErrorMessage(null)
  }

  return (
    <div
      className={`${SETTINGS_SECTION_STYLES.container} flex flex-col gap-4`}
      data-testid="settings-venue-information-editor"
    >
      <div>
        <h2 className="text-xl font-semibold mt-0 mb-0" data-testid="settings-venue-information-editor-title">
          {i18n.t("app.settings.venues.venue_information.title")}
        </h2>
        <p className="text-xs font-medium text-[#616161] mt-1">
          {i18n.t("app.settings.venues.venue_information.subtitle")}
        </p>
      </div>

      {successMessage ? (
        <Alert
          severity="success"
          onClose={() => setSuccessMessage(null)}
          data-testid="settings-venue-information-alert-success"
        >
          {successMessage}
        </Alert>
      ) : null}
      {errorMessage ? (
        <Alert
          severity="error"
          onClose={() => setErrorMessage(null)}
          data-testid="settings-venue-information-alert-error"
        >
          {errorMessage}
        </Alert>
      ) : null}

      {definitions.map((def) => (
        <CategoryCard
          key={def.key}
          definition={def}
          selected={localState[def.key] || []}
          onChange={handleCategoryChange(def.key)}
        />
      ))}

      <div className="flex justify-end gap-3">
        <Button
          variant="outlined"
          color="primary"
          onClick={handleReset}
          disabled={saving || !dirty}
          data-testid="settings-venue-information-btn-cancel"
        >
          {i18n.t("app.settings.venues.venue_information.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="settings-venue-information-btn-save"
        >
          {saving ? <CircularProgress size={20} /> : i18n.t("app.settings.venues.venue_information.save")}
        </Button>
      </div>
    </div>
  )
}

export default VenueInformation
